import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import { getUserDisplayName } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import { calculateParticipantRentAmount } from "../../helper/profileCalculations";
import { convertCurrencyFormat } from "../../helper/common";

const AccordionParticipantRow = ({
  profile,
  participant,
  isPending,
  isOwner,
}) => {
  const { _t } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, checkIeDetails } = authContext;
  const landLordContext = useContext(LandLordContext);
  const { deaWorkspace } = landLordContext;
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const tenantInfo = participant.tenant_info;

  const rentAmount = calculateParticipantRentAmount(
    tenantInfo,
    participant,
    user,
    _t,
    deaWorkspace
  );

  const calculateScore = (userDetails) => {
    if (checkIeDetails(userDetails)) {
      const totalScore =
        (userDetails?.paymentScores?.creditScore?.value || 0) / 2;
      return totalScore > 850 ? 850 : totalScore;
    }
    return userDetails?.paymentScores?.creditScore?.value || 0;
  };

  const getConsentStatusDisplay = () => {
    if (isPending) {
      return (
        <span className="participant-status-badge participant-status-pending">
          {_t("pending")}
        </span>
      );
    }
    return (
      <span className="participant-status-badge participant-status-active">
        {_t("active", "Active")}
      </span>
    );
  };

  const participantName = isPending
    ? participant.email
    : getUserDisplayName(tenantInfo);

  const participantRole = isPending
    ? _t("pending")
    : isOwner
      ? _t("owner")
      : _t("flatmate", "Compañero/a");

  const initials = (participantName || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");

  const handleOpenProfile = () => {
    if (isPending || !tenantInfo?._id) return;

    navigate(
      `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}&tenant_id=${tenantInfo?._id}`
    );
  };

  return (
    <>
      <tr
        className={`hideOnMobile second-row relative participant-row participant-row-clean ${
          !isPending && tenantInfo?._id ? "cursor-pointer" : ""
        }`}
        onClick={handleOpenProfile}
      >
        <td>
          <div
            className="d-flex align-items-center custom-gap-3"
            style={{ marginInlineEnd: "0.5vw", paddingLeft: "1.2vw" }}
          >
            <div className="participant-initials-avatar d-flex align-items-center justify-content-center">
              {initials || "U"}
            </div>
            <div className="d-flex flex-column">
              <span className="participant-name">{participantName}</span>
              <span className="participant-role">{participantRole}</span>
            </div>
          </div>
        </td>

        <td className="skor-details-column">
          {isPending ? "-" : calculateScore(tenantInfo)}
        </td>

        <td>
          {isPending
            ? "-"
            : `€${convertCurrencyFormat(rentAmount.combinedIncome)}`}
        </td>

        <td>
          {isPending
            ? "-"
            : `€${convertCurrencyFormat(rentAmount.effort_rate)}`}
        </td>

        <td>{isPending ? "-" : tenantInfo?.sharers?.length || 0}</td>

        <td>
          {isPending
            ? "-"
            : Array.isArray(tenantInfo?.gurantor_id)
              ? tenantInfo.gurantor_id.length
              : 0}
        </td>

        <td></td>

        <td>
          <div className="participant-status-cell">
            {getConsentStatusDisplay()}
          </div>
        </td>

        <td>
          <div className="cursor-pointer">
            <img
              className="arrowRightTable"
              src="/images/arrow-right-green.svg"
              alt="Open participant"
            />
          </div>
        </td>
      </tr>

      <div
        className={`hideOnDesktop accordion-tile ${
          !isPending && tenantInfo?._id ? "cursor-pointer" : ""
        }`}
        onClick={handleOpenProfile}
      >
        <div
          className="mobile-profile-info-box d-flex align-items-center mobile-profile-gap"
          style={{ paddingLeft: "2vw" }}
        >
          <div className="participant-initials-avatar d-flex align-items-center justify-content-center">
            {initials || "U"}
          </div>
          <div className="d-flex flex-column">
            <span className="participant-name">{participantName}</span>
            <span className="participant-role">{participantRole}</span>
          </div>
        </div>

        <div className="mobile-profile-details">
          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("skor")}</span>
            <span className="detail-value">
              {isPending ? "-" : calculateScore(tenantInfo)}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("income")}</span>
            <span className="detail-value">
              {isPending
                ? "-"
                : `€${convertCurrencyFormat(rentAmount.combinedIncome)}`}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("maxRent")}</span>
            <span className="detail-value">
              {isPending
                ? "-"
                : `€${convertCurrencyFormat(rentAmount.effort_rate)}`}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("flatmates")}</span>
            <span className="detail-value">
              {isPending ? "-" : tenantInfo?.sharers?.length || 0}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("guarantors")}</span>
            <span className="detail-value">
              {isPending
                ? "-"
                : Array.isArray(tenantInfo?.gurantor_id)
                  ? tenantInfo.gurantor_id.length
                  : 0}
            </span>
          </div>
        </div>

        <div className="mobile-profile-footer">
          <div className="participant-status-cell">
            {getConsentStatusDisplay()}
          </div>
          {!isPending && tenantInfo?._id && (
            <div className="cursor-pointer">
              <img
                className="arrowRightTable"
                src="/images/arrow-right-green.svg"
                alt="Open participant"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccordionParticipantRow;
